<script setup lang="ts">
import UIBrow from '@/components/ui/UIBrow.vue'
import useLanguage from '@/composables/useLanguage'
import { colors } from '@/utils/colors'
import { DropdownEventKey } from '@/utils/eventBusKeys/dropdownEventKey'
import { ToolTipEventKey } from '@/utils/eventBusKeys/tooltipKey'
import { ITooltipDropdownItem, TTooltipDropdownActiveStyle } from '@lifk/types'
import {
  PhArrowDown,
  PhArrowUp,
  PhCheckCircle,
  PhCircle
} from '@phosphor-icons/vue'
import { useEventBus } from '@vueuse/core'
import { computed, onMounted, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    dropdownId: string
    options: ITooltipDropdownItem[]
    activeStyle?: TTooltipDropdownActiveStyle
    dropdownTitle?: string
  }>(),
  {
    activeStyle: 'default'
  }
)

const i18n = useLanguage('UIDropdownItems')
const toolTipEventBus = useEventBus(ToolTipEventKey)
const dropdownEventBus = useEventBus(DropdownEventKey)
const dropdownOptionsDiv = ref<HTMLElement | undefined>()
const isToggled = ref(false)
const currentSelected = ref<string | null>(props.options[0].option)
const focusedIndex = ref(0)

const optionsElements = computed(() => {
  if (dropdownOptionsDiv.value?.children) {
    return Array.from(dropdownOptionsDiv.value.children).map(
      (el) => el as HTMLElement
    )
  }
  return []
})

const optionSelected = (selected: string) => {
  if (currentSelected.value === selected) {
    isToggled.value = !isToggled.value
  } else {
    isToggled.value = false
    currentSelected.value = selected
  }

  dropdownEventBus.emit({ id: props.dropdownId, selected })
  closeDropdown()
}

const activeOptionId = computed(() => {
  // return if index is out of bounds
  if (!props.options[focusedIndex.value]) return undefined
  return `${props.dropdownId}-option-${focusedIndex.value}`
})

const closeDropdown = () => {
  toolTipEventBus.emit({ type: 'hide' })
}

const onKeyDown = (e: KeyboardEvent) => {
  const prevFocus = focusedIndex.value
  let nextFocus = prevFocus

  if (e.key === 'Tab') {
    closeDropdown()
    return
  }

  switch (e.key) {
    case 'ArrowUp':
      e.preventDefault()
      nextFocus = prevFocus - 1
      break
    case 'ArrowDown':
      e.preventDefault()
      if (prevFocus < props.options.length - 1) {
        nextFocus = prevFocus + 1
      }
      break
    case 'Enter':
    case ' ':
      e.preventDefault()
      if (props.options[prevFocus]) {
        optionSelected(props.options[prevFocus].option)
      }
      return
  }

  if (nextFocus < 0) {
    closeDropdown()
  } else if (nextFocus !== prevFocus) {
    focusedIndex.value = nextFocus
  }
}

const onMouseMove = () => {
  if (optionsElements.value && optionsElements.value.length > 0) {
    optionsElements.value.forEach((el) => {
      el.blur()
    })
  }
}

onMounted(() => {
  dropdownOptionsDiv.value?.focus()
  // focusFirstChild(dropdownOptionsDiv.value)
  focusedIndex.value = 0
})
</script>

<template>
  <div
    ref="dropdownOptionsDiv"
    role="listbox"
    tabindex="0"
    :aria-activedescendant="activeOptionId"
    :aria-label="i18n.dropdownItems"
    class="py-[10px]"
    @keydown.stop="onKeyDown"
  >
    <UIBrow
      v-if="dropdownTitle"
      :text="dropdownTitle"
      size="sm"
      class="text-default pl-4"
    />
    <div
      v-for="({ option, selected }, index) in options"
      :id="`${dropdownId}-option-${index}`"
      :key="option"
      role="option"
      :aria-selected="selected"
      class="px-4 py-3 text-body hover:bg-poppy-tint-5 focus:bg-poppy-tint-5 cursor-pointer"
      :class="[
        selected && activeStyle === 'default'
          ? 'font-medium text-emphasis bg-poppy-tint-5'
          : 'font-normal',
        {
          'bg-poppy-tint-5': focusedIndex === index
        },
        { 'flex justify-start items-center': activeStyle === 'checkmark' }
      ]"
      @click.stop.prevent="() => optionSelected(option)"
      @mousemove="onMouseMove"
    >
      <template v-if="activeStyle === 'checkmark'">
        <div class="flex items-center w-full">
          <div class="w-4 mr-3">
            <PhCheckCircle
              v-if="selected"
              :size="16"
              weight="fill"
              :color="`#${colors.slate}`"
            />
            <PhCircle
              v-else
              :size="16"
              weight="bold"
              :color="`#${colors.slate}`"
            />
          </div>
          <span>{{ option }}</span>
          <div v-if="selected && dropdownTitle" class="flex ml-auto pl-2">
            <component
              :is="
                currentSelected === option && isToggled
                  ? PhArrowUp
                  : PhArrowDown
              "
              :size="16"
              weight="bold"
              :color="`#${colors.slate}`"
            />
          </div>
        </div>
      </template>

      <template v-else>
        <span>{{ option }}</span>
      </template>
    </div>
  </div>
</template>
